import type { NavigateFunction } from 'react-router-dom'
import { api } from '../utils/api'
import { fromTradeApiResponse } from '../pages/Transactions/createTransaction/tradeMappers'
import type { TradeItem, TradeType } from '../pages/Transactions/createTransaction/types'
import { activeTradeStorageKey } from '../pages/Transactions/createTransaction/helpers'
import { tradeCreatePath } from '../pages/Transactions/createTransaction/tradeRoutes'

export type AddScannedProductOptions = {
  preferredTrade?: TradeType | null
  suppressNavigation?: boolean
  allowCreateTrade?: boolean
}

export type AddScannedProductDeps = {
  getTargetTrade: () => TradeType | null
  setCurrentTrade: (trade: TradeType) => void
  navigate: NavigateFunction
  organizationId: string | undefined
}

export type AddScannedProductResult = {
  tradeId: string
  itemName: string
  quantity: number
  merged: boolean
}

/**
 * Resolve scan → inventory row, enforce stock, PATCH/POST line on draft trade, optional navigate to trade URL.
 */
export async function addScannedProductToTrade(
  productId: string,
  options: AddScannedProductOptions | undefined,
  deps: AddScannedProductDeps,
): Promise<AddScannedProductResult> {
  let targetTrade = options?.preferredTrade || deps.getTargetTrade()

  if (targetTrade && targetTrade.status !== 'draft') {
    targetTrade = null
  }

  const allowCreateTrade = options?.allowCreateTrade !== false

  if (!targetTrade && allowCreateTrade) {
    const createRes = await api.post('/trades', { type: 'trade' })
    const createdTrade = createRes.data?.data as TradeType | undefined
    if (!createdTrade) {
      throw new Error('Failed to create transaction for scanned item')
    }
    targetTrade = fromTradeApiResponse(createdTrade)
    deps.setCurrentTrade(targetTrade)
  }

  if (!targetTrade) {
    throw new Error('Start a transaction first, then scan to add cards')
  }

  const scanRes = await api.get(`/scan/${productId}`)
  const scanData = scanRes.data?.data

  if (!scanData?.card || !scanData?.product) {
    throw new Error('Scanned item data could not be loaded')
  }

  const effectiveProductId = scanData.product.id || productId
  const availableQty = typeof scanData.product.quantity === 'number' ? scanData.product.quantity : 0

  const normalizedTrade = fromTradeApiResponse(targetTrade)
  const normalizedCondition = String(scanData.product.condition || 'Near Mint').trim().toLowerCase()
  const normalizedPrinting = String(scanData.product.printing || 'Normal').trim().toLowerCase()

  const existingItem = (normalizedTrade.storeItems || []).find((item: TradeItem) => {
    if (item.side !== 'store') return false
    if (!item.productId || item.productId !== effectiveProductId) return false
    const itemCondition = String(item.condition || '').trim().toLowerCase()
    const itemPrinting = String(item.printing || '').trim().toLowerCase()
    return itemCondition === normalizedCondition && itemPrinting === normalizedPrinting
  })

  if (existingItem && existingItem.quantity + 1 > availableQty) {
    throw new Error(`Cannot exceed available stock (${availableQty}) for this card in inventory.`)
  }

  if (!existingItem && availableQty < 1) {
    throw new Error('This item is out of stock.')
  }

  // The line records this USD listed price. When the store cannot price the card right now (no
  // exchange rate), adding it would ring it up at $0 — refuse instead of guessing.
  if (!existingItem && (scanData.product.priceUnavailable || scanData.product.priceCents === null)) {
    throw new Error('This card has no price right now. Add it from search and enter the price manually.')
  }

  const itemData = {
    productId: effectiveProductId,
    itemName: scanData.card.name,
    setName: scanData.card.setName,
    setCode: scanData.card.setCode,
    cardNumber: scanData.card.setNumber,
    condition: scanData.product.condition || 'Near Mint',
    printing: scanData.product.printing || 'Normal',
    quantity: 1,
    valueCents: Math.round(scanData.product.priceCents || 0),
    imageUrl: scanData.card.imageUrl || null,
    side: 'store' as const,
  }

  const response = existingItem
    ? await api.patch(`/trades/${targetTrade.id}/items/${existingItem.id}`, {
        quantity: existingItem.quantity + 1,
      })
    : await api.post(`/trades/${targetTrade.id}/items`, {
        items: [itemData],
      })

  const updatedTrade = response.data?.data?.trade as TradeType | undefined
  const normalizedUpdatedTrade = updatedTrade ? fromTradeApiResponse(updatedTrade) : null

  if (normalizedUpdatedTrade) {
    deps.setCurrentTrade(normalizedUpdatedTrade)
  }

  const orgId = deps.organizationId
  if (orgId) {
    localStorage.setItem(activeTradeStorageKey(orgId), targetTrade.id)
  }

  if (!options?.suppressNavigation) {
    deps.navigate(tradeCreatePath({ tradeId: targetTrade.id }), { replace: true })
  }

  const resultingItem = normalizedUpdatedTrade?.storeItems?.find((item: TradeItem) => {
    if (item.side !== 'store') return false
    if (!item.productId || item.productId !== effectiveProductId) return false
    const itemCondition = String(item.condition || '').trim().toLowerCase()
    const itemPrinting = String(item.printing || '').trim().toLowerCase()
    return itemCondition === normalizedCondition && itemPrinting === normalizedPrinting
  })

  return {
    tradeId: targetTrade.id,
    itemName: scanData.card.name,
    quantity: resultingItem?.quantity ?? 1,
    merged: Boolean(existingItem),
  }
}
